/**
 * WebAuthn / FIDO2 browser helpers — Track 5.6.
 *
 * Wraps `navigator.credentials.create()` / `.get()` so the routes can stay
 * UI-only. Handles the base64url <-> ArrayBuffer dance that the WebAuthn
 * spec requires on every byte field.
 *
 * Note: WebAuthn requires a **secure context** (HTTPS, or `localhost`).
 * `isWebAuthnSupported()` reflects that; the caller is expected to surface
 * a friendly error when it returns false.
 */

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    !!window.isSecureContext &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  )
}

// ── base64url <-> ArrayBuffer ────────────────────────────────────────────────

function b64urlToBuffer(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4)
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}

function bufferToB64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Options decoding (server JSON -> WebAuthn API call) ─────────────────────

interface ServerCredDescriptor {
  id: string
  type?: string
  transports?: string[]
}

function decodeAllowOrExclude(list: ServerCredDescriptor[] | undefined): PublicKeyCredentialDescriptor[] {
  return (list || []).map(d => ({
    id: b64urlToBuffer(d.id),
    type: 'public-key',
    transports: d.transports as AuthenticatorTransport[] | undefined,
  }))
}

function decodeCreationOptions(opts: any): PublicKeyCredentialCreationOptions {
  return {
    ...opts,
    challenge: b64urlToBuffer(opts.challenge),
    user: { ...opts.user, id: b64urlToBuffer(opts.user.id) },
    excludeCredentials: decodeAllowOrExclude(opts.excludeCredentials),
  }
}

function decodeRequestOptions(opts: any): PublicKeyCredentialRequestOptions {
  return {
    ...opts,
    challenge: b64urlToBuffer(opts.challenge),
    allowCredentials: decodeAllowOrExclude(opts.allowCredentials),
  }
}

// ── Response encoding (WebAuthn API result -> server JSON) ──────────────────

function encodeAttestation(cred: PublicKeyCredential): any {
  const att = cred.response as AuthenticatorAttestationResponse
  let transports: string[] = []
  // `getTransports()` is only available on attestation responses in newer
  // browsers — fall back to [] if unavailable.
  // @ts-ignore — older lib.dom may lack the typing.
  if (typeof att.getTransports === 'function') {
    try {
      // @ts-ignore
      transports = att.getTransports() || []
    } catch { /* ignore */ }
  }
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(att.clientDataJSON),
      attestationObject: bufferToB64url(att.attestationObject),
      transports,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  }
}

function encodeAssertion(cred: PublicKeyCredential): any {
  const a = cred.response as AuthenticatorAssertionResponse
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(a.clientDataJSON),
      authenticatorData: bufferToB64url(a.authenticatorData),
      signature: bufferToB64url(a.signature),
      userHandle: a.userHandle ? bufferToB64url(a.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function createPasskey(serverOptions: any): Promise<any> {
  const publicKey = decodeCreationOptions(serverOptions)
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('No credential returned by the authenticator')
  return encodeAttestation(cred)
}

export async function getPasskey(serverOptions: any): Promise<any> {
  const publicKey = decodeRequestOptions(serverOptions)
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('No credential returned by the authenticator')
  return encodeAssertion(cred)
}
